/* eslint-disable react-refresh/only-export-components --
   Widget module: the FormWidget registration and its renderer are one plugin. */
import { FormWidgetService, FormWidgetProps } from "./form-widget-service";
import type { TypeTree, TypeTreeField } from "../type-tree";

// ── record list: rows of records, for a field of a checkpoint list of records ([UsageBound], ...) ──
// FEATURE-20261021-check-config-type-and-fileset-form
//
// One row per element, its fields inline: a Str is a text box, a Num a number box (blank is null for
// Num | Null, left out otherwise), a Bool a checkbox, an enum with no fields a choice of its cases;
// anything else is JSON. A new row starts from the record's defaults. The value is the array of the
// rows' records. A validate message about one row ("[2].red: ..." from a message at bounds[2].red)
// shows under that row, with the field it names; any other under the list.

const ROW_MESSAGE = /^\[(\d+)\](?:\.([\w.[\]]+))?:\s*/;

type Row = Record<string, unknown>;

function elementOf(config: unknown): { fields: TypeTreeField[]; defaults: Record<string, unknown> } {
  const tree = (config as { element?: TypeTree } | undefined)?.element;
  if (tree && tree.kind === "record") return { fields: tree.fields, defaults: tree.defaults ?? {} };
  return { fields: [], defaults: {} };
}

function rowsOf(value: unknown): Row[] {
  if (!Array.isArray(value)) return [];
  return value.map((r) => (r && typeof r === "object" && !Array.isArray(r) ? (r as Row) : {}));
}

function inner(t: TypeTree): { tree: TypeTree; nullable: boolean } {
  return t.kind === "nullable" ? { tree: inner(t.of).tree, nullable: true } : { tree: t, nullable: false };
}

/** A new row: each field's default, where the record has one. */
export function newRow(fields: TypeTreeField[], defaults: Record<string, unknown>): Row {
  const row: Row = {};
  for (const f of fields) {
    if (f.name in defaults) row[f.name] = defaults[f.name];
    else if (inner(f.type).nullable) row[f.name] = null;
  }
  return row;
}

function label(name: string): string {
  const words = name.replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function Cell(props: { field: TypeTreeField; value: unknown; disabled: boolean; testId: string; onChange: (v: unknown) => void }) {
  const { field, value, disabled, testId, onChange } = props;
  const { tree, nullable } = inner(field.type);
  const common = { disabled, "data-testid": testId, "aria-label": label(field.name), title: label(field.name) };
  switch (tree.kind) {
    case "num":
      return (
        <input
          {...common}
          type="number"
          placeholder={label(field.name)}
          value={typeof value === "number" ? String(value) : ""}
          onChange={(e) => onChange(e.target.value === "" ? (nullable ? null : undefined) : Number(e.target.value))}
          style={{ flex: 1, minWidth: 0 }}
        />
      );
    case "bool":
      return <input {...common} type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} />;
    case "enum": {
      const chosen = value && typeof value === "object" ? String((value as { case?: unknown }).case ?? "") : "";
      return (
        <select {...common} value={chosen} onChange={(e) => onChange(e.target.value === "" ? (nullable ? null : undefined) : { case: e.target.value })} style={{ flex: 1, minWidth: 0 }}>
          <option value="">{label(field.name)}</option>
          {tree.cases.map((c) => (
            <option key={c.name} value={c.name}>
              {label(c.name)}
            </option>
          ))}
        </select>
      );
    }
    case "str":
    case "named":
    case "date":
    case "time":
    case "timestamp":
    case "money":
      return (
        <input
          {...common}
          type="text"
          placeholder={label(field.name)}
          value={value === undefined || value === null ? "" : String(value)}
          onChange={(e) => onChange(e.target.value === "" ? (nullable ? null : undefined) : e.target.value)}
          style={{ flex: 2, minWidth: 0 }}
        />
      );
    default:
      return (
        <input
          {...common}
          type="text"
          placeholder={`${label(field.name)} (JSON)`}
          value={value === undefined || value === null ? "" : JSON.stringify(value)}
          onChange={(e) => {
            try {
              onChange(e.target.value === "" ? undefined : JSON.parse(e.target.value));
            } catch {
              /* keep the last value that parsed */
            }
          }}
          style={{ flex: 2, minWidth: 0 }}
        />
      );
  }
}

export function RecordListWidget({ formItem, formData, formErrors, setValue }: FormWidgetProps) {
  const { fields, defaults } = elementOf(formItem.config);
  const rows = rowsOf(formData[formItem.name]);
  const disabled = formItem.disabled === true;
  // each message keeps its level; a message about one row goes under it, naming the field it is about
  type Msg = { text: string; severity: string };
  const byRow = new Map<number, Msg[]>();
  const rest: Msg[] = [];
  for (const e of formErrors[formItem.name] ?? []) {
    const message = e.message ?? "";
    const severity = e.severity ?? "error";
    const m = ROW_MESSAGE.exec(message);
    if (m) {
      const text = message.replace(ROW_MESSAGE, "");
      byRow.set(Number(m[1]), [...(byRow.get(Number(m[1])) ?? []), { text: m[2] ? `${label(m[2])}: ${text}` : text, severity }]);
    } else rest.push({ text: message, severity });
  }
  const update = (next: Row[]) => setValue(next);
  const setCell = (i: number, name: string, v: unknown) => {
    const next = rows.map((r) => ({ ...r }));
    if (v === undefined) delete next[i]![name];
    else next[i]![name] = v;
    update(next);
  };
  return (
    <div className="dfr-record-list" data-testid={`records-${formItem.name}`}>
      {rows.map((row, i) => (
        <div key={i} className="dfr-list-row" data-testid={`records-row-${i}`} style={{ display: "flex", flexDirection: "column", gap: 2, marginBottom: 4 }}>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {fields.map((f) => (
              <Cell key={f.name} field={f} value={row[f.name]} disabled={disabled} testId={`records-${i}-${f.name}`} onChange={(v) => setCell(i, f.name, v)} />
            ))}
            <button
              type="button"
              className="dfr-btn"
              disabled={disabled}
              aria-label={`remove row ${i + 1}`}
              data-testid={`records-remove-${i}`}
              onClick={() => update(rows.filter((_, j) => j !== i))}
            >
              ×
            </button>
          </div>
          {byRow.get(i)?.map((msg, k) => (
            <div key={k} className={`dfr-field-msg ${msg.severity}`} data-testid={`records-issue-${i}`}>
              {msg.text}
            </div>
          ))}
        </div>
      ))}
      <button type="button" className="dfr-btn" disabled={disabled} data-testid="records-add" onClick={() => update([...rows, newRow(fields, defaults)])}>
        + Add
      </button>
      {rest.map((msg, k) => (
        <div key={k} className={`dfr-field-msg ${msg.severity}`}>
          {msg.text}
        </div>
      ))}
    </div>
  );
}

FormWidgetService.get().register({ name: "records", render: (props) => <RecordListWidget {...props} /> });
